import { CoralogixRum } from '@coralogix/browser';
import {
    MewsChainId,
    MewsEnterpriseId,
    MewsProfileId,
    MewsUserId,
    PageRoute,
    PageOwner,
    MewsPortfolioId,
    DeploymentEnvironment,
} from './attributes';
import { DEPLOYMENT_ENVIRONMENT } from './constants';

export interface ObservabilityServiceLabels {
    env?: string;
    userId?: string | null;
    enterpriseId?: string | null;
    chainId?: string | null;
    portfolioId?: string | null;
    profileId?: string | null;
    route?: string | null;
    owner?: string | null;
}

/**
 * Updates Coralogix labels with provided values. Existing labels are preserved.
 */
export const updateLabels = ({
    userId,
    enterpriseId,
    chainId,
    portfolioId,
    profileId,
    env,
    route,
    owner,
}: ObservabilityServiceLabels) => {
    const current = CoralogixRum.getLabels();

    /**
     * DO NOT MODIFY - This solution has been suggested by Coralogix as setting
     * labels in event object doesn't allow to filter the values in the service then
     */
    const next = {
        ...current,
    };

    /**
     * DO NOT MODIFY - Attributes / labels might be deprecated in the future
     * and moved entirely to user context for better filtering, for now they will be
     * reflecting attributes available on BE in one place
     */
    const knownEnv = DEPLOYMENT_ENVIRONMENT[env as keyof typeof DEPLOYMENT_ENVIRONMENT];
    if (knownEnv != null) {
        next[DeploymentEnvironment] = knownEnv;
    }
    if (userId != null) next[MewsUserId] = userId;
    if (enterpriseId != null) next[MewsEnterpriseId] = enterpriseId;
    if (chainId != null) next[MewsChainId] = chainId;
    if (portfolioId != null) next[MewsPortfolioId] = portfolioId;
    if (profileId != null) next[MewsProfileId] = profileId;
    if (route != null) next[PageRoute] = route;
    if (owner != null) next[PageOwner] = owner;

    CoralogixRum.setLabels(next);
};

/**
 * Updates Coralogix labels with provided values. Existing labels are unset.
 */
export function setLabelsStrict({
    userId,
    enterpriseId,
    chainId,
    portfolioId,
    profileId,
    env,
    route,
    owner,
}: ObservabilityServiceLabels) {
    /**
     * DO NOT MODIFY - Attributes / labels might be deprecated in the future * and moved entirely to user context for better filtering, for now they will be * reflecting attributes available on BE in one place
     */
    const attributes = {
        [DeploymentEnvironment]: DEPLOYMENT_ENVIRONMENT[env as keyof typeof DEPLOYMENT_ENVIRONMENT] ?? null,
        [MewsUserId]: userId,
        [MewsEnterpriseId]: enterpriseId,
        [MewsChainId]: chainId,
        [MewsPortfolioId]: portfolioId,
        [MewsProfileId]: profileId,
        [PageRoute]: route,
        [PageOwner]: owner,
    };

    /**
     * DO NOT MODIFY - This solution has been suggested by Coralogix as setting
     * labels in event object doesn't allow to filter the values in the service then
     */
    CoralogixRum.setLabels({ ...CoralogixRum.getLabels(), ...attributes });
}

export interface UserContext {
    userId?: string | null;
    enterpriseId?: string | null;
    chainId?: string | null;
    profileId?: string | null;
}

export const setUserContext = ({ userId, enterpriseId, chainId, profileId }: UserContext) => {
    /**
     * DO NOT MODIFY - These values are used to filter in FE related boards
     * like 'User session' where pattern `${namespace}.${attribute}` doesn't work
     */
    const userContext = {
        user_id: userId ?? '',
        user_name: userId ?? '', // Avoid PII complaints but keep User Sessions filters available
        user_email: userId ?? '', // Avoid PII complaints but keep User Sessions filters available
        user_metadata: {
            enterprise_id: enterpriseId,
            chain_id: chainId,
            profile_id: profileId,
        },
    };

    CoralogixRum.setUserContext(userContext);
};
